import { Company } from '@typeRoots/common/company';
import { Address } from '@typeRoots/common/address';

// Helper function to clean the input based on the field
export const handleInputChange = (
  field: keyof Company,
  value: string,
  onChange: (field: keyof Company, value: string) => void,
) => {
  let cleaned = value.trimStart(); // Remove leading spaces

  switch (field) {
    case 'companyName':
    case 'dba':
      cleaned = cleaned.replace(/\s{2,}/g, ' '); // Prevent multiple spaces
      // Only allow letters, numbers, common symbols, and spaces
      if (/^[\p{L}\p{N}\s&\-'.(),]*$/u.test(cleaned)) {
        onChange(field, cleaned);
      }
      break;

    case 'dotNumber':
      if (/^\d{0,8}$/.test(cleaned)) {
        const noLeadingZeros = cleaned.replace(/^0+/, '');
        onChange(field, noLeadingZeros);
      }
      break;

    case 'mcNumber': {
      const prefix = 'MC';
      const numeric = cleaned.replace(/[^0-9]/g, '');

      // Remove any existing 'MC' and prepend it cleanly
      const formatted = `${prefix}${numeric}`;

      onChange(field, formatted);
      break;
    }

    default:
      onChange(field, cleaned);
  }
};

// Helper function to return the props for each field (e.g., maxLength, variant)
export const getFieldProps = (field: keyof Company) => {
  switch (field) {
    case 'companyName':
      return { variant: 'caption' as const, maxLength: 255 };
    case 'dba':
      return { variant: 'caption' as const, maxLength: 255 };
    case 'dotNumber':
      return { variant: 'caption' as const, maxLength: 8 };
    case 'mcNumber':
      return { variant: 'caption' as const, maxLength: 12 };
    default:
      return { variant: 'caption' as const, maxLength: 100 };
  }
};

export const inputFieldsConfig: { label: string; field: keyof Company; testID?: string }[] = [
  { label: 'Company Name', field: 'companyName', testID: 'landing-company-name-edit' },
  { label: 'DBA', field: 'dba', testID: 'landing-dba-edit' },
  { label: 'DOT Number', field: 'dotNumber', testID: 'landing-dot-edit' },
  { label: 'MC Number', field: 'mcNumber', testID: 'landing-mc-edit' },
];

// This function checks if the DOT number is valid (not null, undefined, or empty string)
// and if it contains only digits and is at most 8 characters long.
// It returns true if the DOT number is valid, otherwise false.
export const isDotNumberValid = (dotNumber: string | null | undefined): boolean => {
  return !!dotNumber && dotNumber.trim().length > 0; // Check if dotNumber is not null, undefined, or empty string
};

/**
 * Normalizes the company object by returning a new object with specific properties and their values.
 * If the input company object is null or undefined, it returns an object with all properties set to undefined.
 *
 * Reason for creation:
 * - The Company type from the backend or Redux may include fields that are `null`, `undefined`, or missing.
 * - Many presentational components in the UI expect fields to be `string | undefined` (not `null`), to avoid TypeScript errors and simplify rendering logic.
 * - By normalizing the company object, we ensure a consistent structure and type-safety for all consumers of this data.
 *
 * What this helps with:
 * - Prevents TypeScript errors when passing company data to components that do not accept `null` values.
 * - Simplifies UI code by removing the need for repeated null checks or conversions.
 * - Guarantees that all expected fields are present (even if undefined), making destructuring and rendering safer and more predictable.
 */
export function normalizeCompany(company: Company | null | undefined): {
  companyName?: string;
  dba?: string;
  dateBusinessStarted?: string;
  dotNumber?: string;
  mcNumber?: string;
  garagingAddress?: Address;
  mailingAddress?: Address;
  twoYearsPreviousAuthority?: boolean;
} {
  return {
    companyName: company?.companyName ?? undefined,
    dba: company?.dba ?? undefined,
    dateBusinessStarted: company?.dateBusinessStarted ?? undefined,
    dotNumber: company?.dotNumber ?? undefined,
    mcNumber: company?.mcNumber ?? undefined,
    garagingAddress: company?.garagingAddress ?? undefined,
    mailingAddress: company?.mailingAddress ?? undefined,
    twoYearsPreviousAuthority: company?.twoYearsPreviousAuthority ?? undefined,
  };
}
